/**
 * /dashboard/submissions: the mentor's submissions.
 *
 * Without a module: every cohort and 1:1 student they teach, each module with
 * what waits for review. With ?kind=topic|template&module=<id>: that module's
 * assessment results and assignment submissions (ModuleReport).
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControlLabel,
  InputAdornment,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SearchIcon from "@mui/icons-material/Search";
import NavbarLoggedInNew from "components/Navbar-new/NavbarLoggedInNew";

import * as subsApi from "./submissionsApi";
import ModuleReport from "./ModuleReport";
import { plural } from "./format";
import { FONT, INK, PendingBadge } from "./parts";

const pending = (m) => m.answers_to_review + m.assignments_to_review;

const ModuleRow = ({ module, onOpen }) => (
  <Box
    role="button" tabIndex={0} data-testid="module-row"
    onClick={() => onOpen(module)} onKeyDown={(e) => (e.key === "Enter" ? onOpen(module) : null)}
    sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1, borderRadius: "8px", cursor: "pointer",
          flexWrap: "wrap", "&:hover": { background: "#f0f7fc" } }}
  >
    <Typography sx={{ flex: "1 1 220px", fontSize: 14, color: INK.primary }}>{module.name}</Typography>
    <Typography sx={{ fontSize: 12, color: INK.muted }}>
      {plural(module.assessment_count, "assessment")} · {plural(module.assignment_count, "assignment")}
    </Typography>
    <PendingBadge count={module.answers_to_review} what={module.answers_to_review === 1 ? "answer" : "answers"} />
    <PendingBadge count={module.assignments_to_review} what={module.assignments_to_review === 1 ? "assignment" : "assignments"} />
    <ChevronRightIcon sx={{ color: INK.muted }} />
  </Box>
);

const Group = ({ icon, title, subtitle, modules, onOpen, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  // A search or the "waiting" filter opens the groups it matches.
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);
  const waiting = modules.reduce((n, m) => n + pending(m), 0);
  return (
    <Box sx={{ border: "1px solid #e4eaf0", borderRadius: "12px", mb: 1.5, background: "#fff" }} data-testid="group">
      <Box onClick={() => setOpen(!open)} role="button" tabIndex={0}
           onKeyDown={(e) => (e.key === "Enter" ? setOpen(!open) : null)}
           sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, cursor: "pointer" }}>
        {icon}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 15, fontFamily: FONT }}>{title}</Typography>
          <Typography sx={{ fontSize: 12, color: INK.muted }}>{subtitle}</Typography>
        </Box>
        {waiting > 0 && <PendingBadge count={waiting} what="items" />}
        <ExpandMoreIcon sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", color: INK.muted }} />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 1, pb: 1 }}>
          {modules.length === 0
            ? <Typography sx={{ fontSize: 13, color: INK.muted, p: 1.5 }}>No modules.</Typography>
            : modules.map((m) => <ModuleRow key={`${m.kind}-${m.id}`} module={m} onOpen={onOpen} />)}
        </Box>
      </Collapse>
    </Box>
  );
};

const Overview = ({ onOpen }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [onlyPending, setOnlyPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    subsApi.getOverview()
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(subsApi.errorMessage(e, "Couldn't load your submissions.")));
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!data) return null;
    const q = query.trim().toLowerCase();
    const keep = (title, modules) => {
      const titleMatch = !q || title.toLowerCase().includes(q);
      return modules.filter((m) => (titleMatch || m.name.toLowerCase().includes(q)) && (!onlyPending || pending(m) > 0));
    };
    return {
      cohorts: data.cohorts.map((c) => ({ ...c, modules: keep(c.batch.name, c.modules) }))
        .filter((c) => c.modules.length || (!onlyPending && !q)),
      students: data.students.map((s) => ({ ...s, modules: keep(`${s.student.name} ${s.student.email}`, s.modules) }))
        .filter((s) => s.modules.length || (!onlyPending && !q)),
    };
  }, [data, query, onlyPending]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!filtered) return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;

  const totalWaiting = [...data.cohorts, ...data.students].flatMap((g) => g.modules).reduce((n, m) => n + pending(m), 0);

  return (
    <Box>
      <Typography sx={{ fontSize: 24, fontWeight: 600, fontFamily: FONT }}>Submissions</Typography>
      <Typography sx={{ fontSize: 14, color: INK.secondary, mb: 2 }}>
        {totalWaiting ? `${plural(totalWaiting, "item")} waiting for your review. ` : "Nothing waiting for review. "}
        Open a module to see results, time spent and attempts, and to review work.
      </Typography>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
        <TextField size="small" placeholder="Search students, cohorts, modules" value={query}
                   onChange={(e) => setQuery(e.target.value)} sx={{ flex: "1 1 280px", background: "#fff" }}
                   InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <FormControlLabel control={<Switch checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} />}
                          label="Only waiting for review" />
      </Box>

      {filtered.cohorts.length > 0 && (
        <>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: INK.muted, textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>
            Cohorts
          </Typography>
          {filtered.cohorts.map((c) => (
            <Group key={`c-${c.batch.id}`} icon={<GroupsOutlinedIcon sx={{ color: "#0172A6" }} />}
                   title={c.batch.name} subtitle={`${plural(c.student_count, "student")} · ${plural(c.modules.length, "module")}`}
                   modules={c.modules} onOpen={onOpen} defaultOpen={Boolean(query) || onlyPending || data.cohorts.length === 1} />
          ))}
        </>
      )}

      {filtered.students.length > 0 && (
        <>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: INK.muted, textTransform: "uppercase", letterSpacing: 0.5, mt: 3, mb: 1 }}>
            1:1 students
          </Typography>
          {filtered.students.map((s) => (
            <Group key={`s-${s.course_id}`} icon={<PersonOutlineIcon sx={{ color: "#0172A6" }} />}
                   title={s.student.name} subtitle={`${s.course_name} · ${s.student.email}`}
                   modules={s.modules} onOpen={onOpen} defaultOpen={Boolean(query) || onlyPending} />
          ))}
        </>
      )}

      {!filtered.cohorts.length && !filtered.students.length && (
        <Typography sx={{ color: INK.muted, p: 3, textAlign: "center" }}>
          {query || onlyPending ? "Nothing matches." : "You don't mentor any cohorts or 1:1 students yet."}
        </Typography>
      )}
    </Box>
  );
};

const SubmissionsPage = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [toast, setToast] = useState("");
  const kind = params.get("kind");
  const moduleId = params.get("module");
  const open = (m) => setParams({ kind: m.kind, module: String(m.id) });

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F8FAFC", fontFamily: FONT }}>
      <NavbarLoggedInNew />
      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}>
        {kind && moduleId ? (
          <>
            <Button startIcon={<ArrowBackIcon />} onClick={() => (window.history.length > 1 ? navigate(-1) : setParams({}))}
                    sx={{ textTransform: "none", mb: 1, ml: -1 }}>
              Back
            </Button>
            <Button onClick={() => setParams({})} sx={{ textTransform: "none", mb: 1 }}>All submissions</Button>
            <ModuleReport key={`${kind}-${moduleId}`} kind={kind} moduleId={moduleId} initialTab={params.get("tab")} notify={setToast} />
          </>
        ) : (
          <Overview onOpen={open} />
        )}
      </Box>
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast("")} message={toast}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </Box>
  );
};

export default SubmissionsPage;
